/**
 * @sazeo/sdk — Suppliers hooks
 *
 * TanStack Query wrappers for the invoicing addon suppliers resource. All
 * calls route through `/addons/invoicing/suppliers` guarded by
 * requireAddonEnabled("invoicing") and enforced by RLS via the caller's JWT.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { buildFunctionUrl } from './functionUrl';
import type { SupabaseClientLike } from './people';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface Supplier {
  id: string;
  tenant_id: string;
  name: string;
  contact_name: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  tax_id: string | null;
  currency: string;
  notes: string | null;
  is_active: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface SupplierCreateInput {
  name: string;
  contact_name?: string | null;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
  tax_id?: string | null;
  currency?: string;
  notes?: string | null;
}

export interface SupplierUpdateInput extends Partial<SupplierCreateInput> {
  is_active?: boolean;
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const supplierKeys = {
  all: ['suppliers'] as const,
  list: (tenantId: string) => [...supplierKeys.all, 'list', tenantId] as const,
  detail: (id: string) => [...supplierKeys.all, 'detail', id] as const,
} as const;

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

async function getAuthHeaders(supabase: SupabaseClientLike): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  return token ? { authorization: `Bearer ${token}` } : {};
}

async function apiFetch<T>(
  supabase: SupabaseClientLike,
  path: string,
  init?: RequestInit,
): Promise<T> {
  const headers = await getAuthHeaders(supabase);
  const res = await fetch(buildFunctionUrl(path), {
    ...init,
    headers: { 'content-type': 'application/json', ...headers, ...init?.headers },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({ error: { message: res.statusText } }))) as {
      error?: { message?: string };
    };
    throw new Error(body.error?.message ?? res.statusText);
  }
  if (res.status === 204) return null as T;
  return res.json() as Promise<T>;
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useSuppliers(supabase: SupabaseClientLike, tenantId: string) {
  return useQuery({
    queryKey: supplierKeys.list(tenantId),
    queryFn: () => apiFetch<Supplier[]>(supabase, 'addons/invoicing/suppliers'),
    enabled: Boolean(tenantId),
  });
}

export function useCreateSupplier(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SupplierCreateInput) =>
      apiFetch<Supplier>(supabase, 'addons/invoicing/suppliers', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: supplierKeys.list(tenantId) });
    },
  });
}

export function useUpdateSupplier(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: SupplierUpdateInput }) =>
      apiFetch<Supplier>(supabase, `addons/invoicing/suppliers/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: supplierKeys.detail(vars.id) });
      void qc.invalidateQueries({ queryKey: supplierKeys.list(tenantId) });
    },
  });
}

export function useDeleteSupplier(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(supabase, `addons/invoicing/suppliers/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: (_, id) => {
      void qc.invalidateQueries({ queryKey: supplierKeys.detail(id) });
      void qc.invalidateQueries({ queryKey: supplierKeys.list(tenantId) });
    },
  });
}
